/**
 * Cost Estimation Card Component
 * Displays VM cost breakdown for a training run
 *
 * NC-1294 Slice 2: also shows the Ist (actual) cost and its deviation from the
 * estimate, via ActualCostSection. `totalCost` keeps its Schätzung meaning — the
 * Ist fields are additive, and a response without them renders exactly as before.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import {
  Euro as EuroIcon,
  Computer as ComputerIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { RunActualCost, RunCostComparison } from '../services/types';
import ActualCostSection from './ActualCostSection';

interface CostEstimation {
  runId: string;
  // NC-867 T7: cost-center the run is charged to; absent on legacy runs.
  costCenter?: string;
  vmSize: string;
  vmCount: number;
  hourlyPrice: number;
  hoursPerDay: number;
  days: number;
  costPerVM: number;
  totalCost: number;
  currency: string;
  message?: string;
  // NC-1294: additive Ist fields. Optional so a pre-NC-1294 response still renders.
  actualCost?: RunActualCost;
  comparison?: RunCostComparison | null;
}

interface CostEstimationCardProps {
  runId: string;
}

export const CostEstimationCard: React.FC<CostEstimationCardProps> = ({ runId }) => {
  const [costData, setCostData] = useState<CostEstimation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCostEstimation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/runs-cost/${runId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch cost estimation');
      }

      const data = await response.json();
      setCostData(data.data);
    } catch (err) {
      console.error('Error fetching cost estimation:', err);
      setError(err instanceof Error ? err.message : 'Failed to load cost estimation');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchCostEstimation();
  }, [fetchCostEstimation]);

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Calculating costs...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!costData) {
    return null;
  }

  // NC-643: empty-state when the run has no accounts yet — the user-report
  // ("keine Werte für die VMs") came from a run where vmCount=0 made the
  // card show only a generic "No VMs in this run" alert. Spell out *why*
  // there are no values + how to populate them.
  if (costData.vmCount === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cost Estimation
          </Typography>
          <Alert severity="info">
            Noch keine Teilnehmer im Schulungslauf — sobald Konten angelegt sind,
            erscheinen hier die Kosten je VM und Gesamtkosten.
          </Alert>
          {/*
            NC-1294: a run whose VMs are already gone still accrues trailing charges
            under its RunId tag — exactly the case the API keeps reporting on this
            branch. Hiding the Ist here would drop the only cost information such a
            run has.
          */}
          <ActualCostSection actualCost={costData.actualCost} comparison={costData.comparison} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EuroIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Cost Estimation</Typography>
        </Box>

        {/* NC-867 T7: show which cost-center this run is charged to. Graceful
            when absent (legacy runs created before the CostCenter gate). */}
        {costData.costCenter && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="cost-estimation-costcenter">
            Kostenstelle: <strong>{costData.costCenter}</strong>
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          {/* VM Configuration */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ComputerIcon fontSize="small" color="action" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                VM Configuration
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight="medium">
              {costData.vmSize}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {costData.vmCount} VM{costData.vmCount !== 1 ? 's' : ''}
            </Typography>
          </Grid>

          {/* Hourly Rate */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ScheduleIcon fontSize="small" color="action" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Hourly Rate
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight="medium">
              {costData.hourlyPrice.toFixed(2)} {costData.currency}/h
            </Typography>
            <Typography variant="caption" color="text.secondary">
              per VM
            </Typography>
          </Grid>

          {/* Training Duration */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Training Duration
            </Typography>
            <Typography variant="body1">
              {costData.hoursPerDay}h/day × {costData.days} days
            </Typography>
            <Chip
              label={`${costData.hoursPerDay * costData.days} total hours`}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Grid>

          {/* Cost Per VM */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Cost per VM
            </Typography>
            <Typography variant="h6" color="primary.main">
              {costData.costPerVM.toFixed(2)} {costData.currency}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              for entire training
            </Typography>
          </Grid>

          {/* Total Cost */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                p: 2,
                bgcolor: 'success.light',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Estimated Cost
              </Typography>
              <Typography variant="h4" color="success.dark" fontWeight="bold">
                {costData.totalCost.toFixed(2)} {costData.currency}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {costData.vmCount} VM{costData.vmCount !== 1 ? 's' : ''} × {costData.days} days
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <ActualCostSection actualCost={costData.actualCost} comparison={costData.comparison} />

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            * Estimated costs based on Azure Retail Prices API. Actual costs may vary based on
            regional pricing, reservation discounts, and actual usage hours.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
